import { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithPopup,
  GoogleAuthProvider
} from "firebase/auth";

import auth from "../config/firebase";
import { User } from '../models/accounts/User';
import { userService } from '../apiClient';

const AuthContext = createContext();
const googleProvider = new GoogleAuthProvider();

// Builds a User from an RDS row, falling back to caller-supplied values for
// fields the row may not carry yet (e.g. a just-created profile response).
function buildUserFromRDS(userData, fallbacks = {}) {
  return new User(
    userData.userID || userData.uid || fallbacks.userID,
    userData.userEmail || fallbacks.userEmail,
    userData.userFName || fallbacks.userFName,
    userData.userRolePrimary || fallbacks.userRolePrimary,
    null, null, null, null, null, null, null, null,
    userData.userPhoneNumber || fallbacks.userPhoneNumber || null,
    userData.themePreference || 'system',
    // RDS row wins once the backend stores it; until then fall back to the
    // Firebase account photo (Google sign-in populates this).
    userData.userPhotoURL || fallbacks.userPhotoURL || null
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [currentUserObject, setCurrentUserObject] = useState();
  const [operatorIDs, setOperatorIDs] = useState([]);
  const [selectedOperatorID, setSelectedOperatorID] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimsLoaded, setClaimsLoaded] = useState(false);

  async function createUserInRDS(user, email, name, isOperator = false, phoneNumber = null) {
    try {
      const response = await userService.createProfile({
        uid: user.uid,
        email: email,
        displayName: name,
        userRolePrimary: isOperator ? 'operator' : 'renter',
        userPhoneNumber: phoneNumber
      });
      console.log(`Successfully created user ${name}`);
      return response;
    } catch (error) {
      console.error('Error creating user in RDS:', error);
      throw error;
    }
  }

  async function register(email, password, displayName, isOperator = false, phoneNumber = null) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userData = await createUserInRDS(userCredential.user, email, displayName, isOperator, phoneNumber);
      const userObject = buildUserFromRDS(userData, {
        userEmail: email,
        userFName: displayName,
        userRolePrimary: isOperator ? 'operator' : 'renter',
        userPhoneNumber: phoneNumber,
        userPhotoURL: userCredential.user?.photoURL,
      });
      setCurrentUserObject(userObject);
      return userCredential;
    } catch (error) {
      console.error('Error during registration:', error);
      throw error;
    }
  }

  async function fetchUserFromRDS(user) {
    try {
      return await userService.getProfile(user.uid);
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }

  async function loginWithGoogle(isOperator = false) {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      let userData;

      try {
        userData = await fetchUserFromRDS(user);
      } catch (error) {
        console.log("New Google user detected. Registering in RDS...");
        
        const displayName = user.displayName || user.email.split('@')[0];
        
        userData = await createUserInRDS(user, user.email, displayName, isOperator);
      }

      const userObject = buildUserFromRDS(userData, {
        userEmail: user.email,
        userFName: user.displayName,
        userRolePrimary: isOperator ? 'operator' : 'renter',
        userPhotoURL: user.photoURL,
      });

      setCurrentUserObject(userObject);
      return userCredential;

    } catch (error) {
      console.error('Error during Google Sign-In:', error);
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userData = await fetchUserFromRDS(userCredential.user);
      const userObject = buildUserFromRDS(userData, { userPhotoURL: userCredential.user?.photoURL });
      setCurrentUserObject(userObject);
      return userCredential;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  }

  async function refreshUserProfile() {
    if (!currentUser) return;
    const uid = currentUser.uid;
    const userData = await fetchUserFromRDS(currentUser);
    // Firebase's own live auth state, not the possibly-stale `currentUser`
    // closure — if the user logged out (or switched accounts) while this
    // fetch was in flight, discard the result instead of resurrecting a
    // stale profile (and its theme preference) into the new session.
    if (auth.currentUser?.uid !== uid) return;
    const userObject = buildUserFromRDS(userData, { userPhotoURL: auth.currentUser?.photoURL });
    setCurrentUserObject(userObject);
    return userObject;
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function logout() {
    setCurrentUserObject(null);
    return signOut(auth);
  }

  const refreshOperatorClaims = async (fallbackIDs) => {
    if (!currentUser) return;
    const tokenResult = await currentUser.getIdTokenResult(true);
    let opIds = tokenResult.claims.operatorOf;
    if (!opIds) opIds = [];
    if (!Array.isArray(opIds)) opIds = [opIds];
    opIds = opIds.filter(Boolean);
    if (fallbackIDs) {
      for (const id of fallbackIDs) {
        if (!opIds.includes(id)) opIds.push(id);
      }
    }
    setOperatorIDs(opIds);
    setSelectedOperatorID((prev) => (prev && opIds.includes(prev) ? prev : (opIds[0] || null)));
    return opIds;
  };

  // Fires on every token change (sign-in, sign-out, force-refresh) — keeps operatorIDs in sync
  // without waiting for the hour-long natural token expiry.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setOperatorIDs([]);
        setSelectedOperatorID(null);
        setClaimsLoaded(true);
        return;
      }
      try {
        // false = read the cached result; onIdTokenChanged already fired with the refreshed token
        const tokenResult = await user.getIdTokenResult(false);
        let opIds = tokenResult.claims.operatorOf;
        if (!opIds) opIds = [];
        if (!Array.isArray(opIds)) opIds = [opIds];
        opIds = opIds.filter(Boolean);
        setOperatorIDs(prev =>
          prev.length === opIds.length && prev.every((id, i) => id === opIds[i]) ? prev : opIds
        );
        setSelectedOperatorID(prev => (prev && opIds.includes(prev) ? prev : (opIds[0] || null)));
      } catch (e) {
        console.error('Failed to parse operator claims from token:', e);
      } finally {
        setClaimsLoaded(true);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const userData = await fetchUserFromRDS(user);
          const userObject = buildUserFromRDS(userData, { userPhotoURL: user.photoURL });
          setCurrentUserObject(userObject);
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setCurrentUserObject(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Operator = primary operator role or at least one operator claim.
  const isOperator = (currentUserObject?.getUserRolePrimary?.() === 'operator') || operatorIDs.length > 0;

  const value = {
    currentUser,
    user: currentUser,
    currentUserObject,
    operatorIDs,
    isOperator,
    claimsLoaded,
    selectedOperatorID,
    setSelectedOperatorID,
    refreshOperatorClaims,
    refreshUserProfile,
    getIdToken: async () => {
      if (!currentUser) return null;
      return currentUser.getIdToken();
    },
    login,
    loginWithGoogle,
    register,
    resetPassword,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}